// ============================================================================
// CHANGO V5.0 — <CommandPalette> — D5 global search / Cmd+K jumper
// ============================================================================
// Modal palette mounted inside AppShell. Opened by ⌘K / Ctrl+K anywhere
// under /app. Fetches /api/search with a 150 ms debounce and renders results
// grouped by kind (quote · customer · sku · setting).
//
// §20 #17 defense-in-depth:
//   Even though the backend never returns totals/cost/profit for the /search
//   endpoint, the UI also refuses to render any field whose key would look
//   like sensitive finance data ("total", "profit", "cost", "margin"). If
//   the backend is ever accidentally extended, this UI won't leak.
//
// Keyboard model:
//   ⌘K / Ctrl+K   toggle open/close
//   ESC           close
//   ↑ / ↓         move cursor
//   Enter         navigate to selected item
//
// Focus-trap is minimal: we restore focus to the previously-focused element
// when the modal closes, and we auto-focus the input on open.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, HttpError } from '../api/client.js';

const KIND_LABEL = Object.freeze({
  quote:    '报价',
  customer: '客户',
  sku:      '套餐',
  setting:  '快捷入口',
});

const KIND_ORDER = ['quote', 'customer', 'sku', 'setting'];

// Keys that must NEVER appear in a rendered row. Belt-and-braces against
// future backend mistakes (§20 #17).
const FORBIDDEN_FIELDS = /^(total|profit|cost|margin)/i;

// Strip anything that might smell like sensitive data before rendering.
function scrubItem(item) {
  if (!item || typeof item !== 'object') return item;
  const out = {};
  for (const [k, v] of Object.entries(item)) {
    if (FORBIDDEN_FIELDS.test(k)) continue;
    out[k] = v;
  }
  return out;
}

function groupByKind(items) {
  const groups = new Map();
  for (const it of items) {
    const g = groups.get(it.kind) || [];
    g.push(it);
    groups.set(it.kind, g);
  }
  return KIND_ORDER
    .filter((k) => groups.has(k))
    .map((k) => ({ kind: k, label: KIND_LABEL[k] || k, items: groups.get(k) }));
}

function useDebounced(value, delay = 150) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(0);

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const prevFocusRef = useRef(null);
  const abortRef = useRef(null);

  const debounced = useDebounced(term, 150);

  // ---- Global hotkey binding ------------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- Focus management on open/close --------------------------------------
  useEffect(() => {
    if (open) {
      prevFocusRef.current = document.activeElement;
      // Defer so the input has mounted.
      queueMicrotask(() => inputRef.current?.focus());
    } else {
      // Reset state so next open is clean.
      setTerm('');
      setItems([]);
      setCursor(0);
      setError(null);
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    }
  }, [open]);

  // ---- Fetch on debounced term -------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ q: debounced || '', limit: '15' }).toString();
    api.get(`/api/search?${qs}`, { signal: controller.signal })
      .then((data) => {
        const list = Array.isArray(data?.items) ? data.items.map(scrubItem) : [];
        setItems(list);
        setCursor(0);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        if (err instanceof HttpError && err.code === 'NO_WORKSPACE') {
          // Admin session — palette doesn't apply. Close silently.
          setItems([]);
          return;
        }
        setError(err?.message || '搜索失败');
        setItems([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [debounced, open]);

  const groups = useMemo(() => groupByKind(items), [items]);

  // Flat list the cursor indexes into, so ↑/↓ traverses across groups.
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const pick = useCallback((item) => {
    if (!item?.url) return;
    setOpen(false);
    navigate(item.url);
  }, [navigate]);

  const onKey = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(flat[cursor]);
    }
  }, [flat, cursor, pick]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 pt-24"
      role="dialog"
      aria-modal="true"
      aria-label="全局搜索"
      onMouseDown={(e) => {
        // Click-outside to close. Only close when the click is on the backdrop
        // itself (i.e., target === currentTarget).
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white shadow-2xl overflow-hidden"
        onKeyDown={onKey}
      >
        {/* ---- Input row ---------------------------------------------- */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
          <span className="text-slate-400 text-lg" aria-hidden="true">🔎</span>
          <input
            ref={inputRef}
            type="search"
            autoComplete="off"
            spellCheck="false"
            placeholder="搜索报价、客户、套餐或跳转…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-slate-800 placeholder-slate-400"
            aria-label="搜索"
          />
          <kbd className="hidden sm:inline-block rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs font-mono text-slate-500">
            ESC
          </kbd>
        </div>

        {/* ---- Results list ------------------------------------------- */}
        <div className="max-h-[28rem] overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-xs text-slate-400">搜索中…</div>
          )}
          {!!error && (
            <div className="px-4 py-3 text-sm text-red-600" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && flat.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              {term ? '没有匹配的结果' : '输入关键字以开始搜索'}
            </div>
          )}
          {groups.map((group) => (
            <Group
              key={group.kind}
              group={group}
              flat={flat}
              cursor={cursor}
              onPick={pick}
              onHover={setCursor}
            />
          ))}
        </div>

        {/* ---- Footer hints ------------------------------------------- */}
        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
          <span>
            <kbd className="kbd">↑</kbd>
            <kbd className="kbd">↓</kbd> 选择 ·
            <kbd className="kbd">Enter</kbd> 打开
          </span>
          <span>
            <kbd className="kbd">⌘</kbd>/<kbd className="kbd">Ctrl</kbd>
            <kbd className="kbd">K</kbd> 随时唤起
          </span>
        </div>
      </div>
    </div>
  );
}

function Group({ group, flat, cursor, onPick, onHover }) {
  return (
    <div className="py-1">
      <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {group.label}
      </div>
      <ul className="divide-y divide-slate-50">
        {group.items.map((item) => {
          const globalIndex = flat.indexOf(item);
          const active = globalIndex === cursor;
          return (
            <li key={`${item.kind}:${item.id}`}>
              <button
                type="button"
                onMouseEnter={() => onHover(globalIndex)}
                onClick={() => onPick(item)}
                className={[
                  'flex w-full items-center gap-3 px-4 py-2 text-left text-sm',
                  active ? 'bg-chango-50 text-chango-800' : 'text-slate-700 hover:bg-slate-50'
                ].join(' ')}
              >
                <span className="flex-1 min-w-0">
                  <span className="block font-medium truncate">{item.title}</span>
                  {item.subtitle && (
                    <span className="block text-xs text-slate-500 truncate">
                      {item.subtitle}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-mono text-slate-400">
                  {item.kind}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
